import type { RootState } from '../store/store';
import { examsSelectors } from '../slices/exams/examsSlice';
import {
  isComlexExam,
  isNbmeExam,
  isNclexExam,
  isPreclinicalModeExam,
} from '../utils/examUtils';
import { selectSelectedLectureIds } from '../slices/lectures/lecturesSlice';
import { selectActiveBlock } from '../slices/blocks/blocksSlice';
import { selectStudyContext } from '../slices/studyContext/studyContextSlice';
import type {
  FlashcardRunOrder,
  FlashcardRunScope,
} from '../slices/flashcardRun/flashcardRunSlice';

/**
 * Pure decision and URL-parsing logic for the study route loaders.
 *
 * Every export here is a pure function of `(url | scope | RootState)` — no
 * `store`, no dispatch, no I/O — so the routing/parsing decisions that drive
 * what the study route loads can be unit-tested directly. The loaders in
 * `studyLoaders.ts` call these with `store.getState()` and own the side effects.
 */

export const isBlockExam = (state: RootState) => {
  const primaryExam = examsSelectors.selectPrimaryExam(state);
  return (
    isNbmeExam(primaryExam) ||
    isComlexExam(primaryExam) ||
    isNclexExam(primaryExam)
  );
};

export const isCustomPrimaryExam = (state: RootState) => {
  const primaryExam = examsSelectors.selectPrimaryExam(state);
  return isPreclinicalModeExam(primaryExam);
};

export const getLectureIds = (url: URL, state: RootState) => {
  const lectureIdsParam = url.searchParams.get('lectureIds');
  if (lectureIdsParam) {
    return lectureIdsParam
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }
  const lectureIdsMulti = url.searchParams.getAll('lectureId');
  if (lectureIdsMulti.length > 0) {
    return lectureIdsMulti.map((id) => id.trim()).filter(Boolean);
  }
  return selectSelectedLectureIds(state);
};

export const getExplicitSearchIds = (
  url: { searchParams: URLSearchParams },
  pluralKey: string,
  singularKey: string
) => {
  const pluralValue = url.searchParams.get(pluralKey);
  if (pluralValue) {
    return pluralValue
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  return url.searchParams
    .getAll(singularKey)
    .map((id) => id.trim())
    .filter(Boolean);
};

export const shouldUseLectureData = (url: URL, state: RootState) => {
  const studyContext = selectStudyContext(state);
  const hasBlockId = url.searchParams.has('blockId');
  if (hasBlockId || studyContext.mode === 'standalone') {
    return false;
  }

  const hasLectureParams =
    url.searchParams.has('lectureIds') || url.searchParams.has('lectureId');

  if (hasLectureParams) return true;
  if (!isBlockExam(state)) {
    const lectureIds = getLectureIds(url, state);
    return lectureIds.length > 0;
  }
  return false;
};

/**
 * Not a block id: the marker a standalone flashcard run puts in the study
 * context so the deck knows it is standalone. It has never named anything the
 * block loader could fetch.
 */
const FLASHCARD_RUN_BLOCK_MARKER = 'flashcards';

export const getBlockId = (url: URL, state: RootState) => {
  const fromQuery = url.searchParams.get('blockId');
  if (fromQuery) return fromQuery;

  // A real loaded block still wins, exactly as it did before study sets
  // existed: the QBank routes are reachable with no id in the URL, and the
  // block on screen is the one they mean.
  const activeBlock = selectActiveBlock(state);
  if (activeBlock?.id) return activeBlock.id;

  // Nothing is loaded, so the only candidate left is the study context's own
  // id — and a standalone run that has no block of its own does not have one:
  // a set-driven flashcard or video session (ORA-681) stores null, and a deck
  // stores the marker above. Either would be loaded as if it were a block, and
  // the student's answers written into it.
  const standaloneBlockId = selectStudyContext(state).activeBlockId;

  return standaloneBlockId === FLASHCARD_RUN_BLOCK_MARKER
    ? null
    : (standaloneBlockId ?? null);
};

/**
 * The study set a study route was opened for (ORA-681), or null.
 *
 * Takes anything with `searchParams`, so the loaders can pass a `URL` and the
 * runners the router's own `URLSearchParams`.
 */
export const getStudySetIdFromUrl = (url: {
  searchParams: URLSearchParams;
}): string | null => {
  const setId = url.searchParams.get('setId')?.trim();
  return setId ? setId : null;
};

/** The run scope a `?setId=` flashcard route asks for. */
export const getFlashcardSetScope = (url: {
  searchParams: URLSearchParams;
}): Extract<FlashcardRunScope, { kind: 'set' }> | null => {
  const setId = getStudySetIdFromUrl(url);
  return setId ? { kind: 'set', setId } : null;
};

export const hasPreloadedBlockData = (
  state: RootState,
  blockId?: string | null
) => {
  if (!blockId) return false;
  const studyContext = selectStudyContext(state);

  const activeBlockId =
    studyContext.mode === 'standalone'
      ? studyContext.activeBlockId
      : state.todaySession?.session?.activeBlockId;

  if (!activeBlockId || activeBlockId !== blockId) return false;
  if (!state.blocks?.entities?.[blockId]) return false;
  if (state.vignettes?.blockId !== blockId) return false;

  const vignetteCount = Array.isArray(state.vignettes?.ids)
    ? state.vignettes.ids.length
    : 0;

  return vignetteCount > 0;
};

export const hasStaleTodaySessionExam = (state: RootState) => {
  const studyContext = selectStudyContext(state);
  if (studyContext.mode === 'standalone') return false;

  const primaryExam = examsSelectors.selectPrimaryExam(state);
  if (!primaryExam?.id) return false;

  const sessionExamId = state.todaySession?.session?.examId;
  if (!sessionExamId) return false;

  return sessionExamId !== primaryExam.id;
};

export const buildFlashcardRunScopeKey = (scope: FlashcardRunScope) => {
  if (scope.kind === 'set') {
    return `set:${scope.setId}`;
  }

  if (scope.kind === 'session') {
    if (scope.amount || scope.type || scope.count || scope.order) {
      const countPart =
        scope.amount === 'count' ? String(scope.count ?? 0) : 'all';

      return `session:${scope.studySessionId}:${scope.type ?? 'both'}:${countPart}:${scope.order ?? 'due'}`;
    }
    return `session:${scope.studySessionId}`;
  }

  const examPart = scope.examId ?? '';
  const topicPart = [...(scope.topicIds ?? [])].sort().join(',');
  const lecturePart = [...(scope.lectureIds ?? [])].sort().join(',');
  const countPart = scope.amount === 'all' ? 'all' : String(scope.count ?? 0);
  return `${scope.kind}:${examPart}:${topicPart}:${lecturePart}:${scope.type}:${countPart}:${scope.order ?? 'due'}`;
};

export const runMatchesScope = (state: RootState, scope: FlashcardRunScope) =>
  state.flashcardRun.scopeKey === buildFlashcardRunScopeKey(scope);

export const getStandaloneFlashcardScopeFromUrl = (
  url: URL
): Extract<FlashcardRunScope, { kind: 'lecture' | 'filters' }> | null => {
  const source = url.searchParams.get('source');
  if (source === 'session') {
    return null;
  }

  const kindParam = url.searchParams.get('kind');
  const examId = url.searchParams.get('examId') ?? undefined;
  const topicIds = getExplicitSearchIds(url, 'topicIds', 'topicId');
  const lectureIds = getExplicitSearchIds(url, 'lectureIds', 'lectureId');
  const amountParam = url.searchParams.get('amount');
  const typeParam = url.searchParams.get('type');
  const countParam = url.searchParams.get('count');
  const orderParam = url.searchParams.get('order');

  const hasStandaloneParams =
    source === 'filters' ||
    kindParam === 'lecture' ||
    kindParam === 'filters' ||
    Boolean(examId) ||
    topicIds.length > 0 ||
    lectureIds.length > 0 ||
    amountParam === 'all' ||
    amountParam === 'count' ||
    typeParam === 'new' ||
    typeParam === 'due' ||
    typeParam === 'both';

  if (!hasStandaloneParams) {
    return null;
  }

  const kind: 'lecture' | 'filters' =
    kindParam === 'lecture' || kindParam === 'filters'
      ? kindParam
      : lectureIds.length > 0 && topicIds.length === 0
        ? 'lecture'
        : 'filters';

  const amount: 'all' | 'count' = amountParam === 'count' ? 'count' : 'all';

  const countValue =
    amount === 'count' && countParam
      ? Math.max(1, Number.parseInt(countParam, 10) || 0)
      : undefined;

  const type: 'new' | 'due' | 'both' =
    typeParam === 'new' || typeParam === 'due' || typeParam === 'both'
      ? typeParam
      : 'both';

  const order: FlashcardRunOrder | undefined =
    orderParam === 'due' || orderParam === 'mixed' || orderParam === 'new'
      ? orderParam
      : undefined;

  return {
    kind,
    examId,
    topicIds: topicIds.length > 0 ? topicIds : undefined,
    lectureIds: lectureIds.length > 0 ? lectureIds : undefined,
    amount,
    count: countValue,
    type,
    order,
  };
};

export const getSessionFlashcardRunOptionsFromUrl = (
  url: URL
): {
  amount?: 'all' | 'count';
  count?: number;
  type?: 'new' | 'due' | 'both' | 'linked';
  order?: FlashcardRunOrder;
} => {
  if (url.searchParams.get('source') !== 'session') {
    return {};
  }

  const amountParam = url.searchParams.get('amount');
  const typeParam = url.searchParams.get('type');
  const countParam = url.searchParams.get('count');
  const orderParam = url.searchParams.get('order');

  const amount: 'all' | 'count' | undefined =
    amountParam === 'all' || amountParam === 'count' ? amountParam : undefined;

  const type: 'new' | 'due' | 'both' | 'linked' | undefined =
    typeParam === 'new' ||
    typeParam === 'due' ||
    typeParam === 'both' ||
    typeParam === 'linked'
      ? typeParam
      : undefined;

  const count =
    amount === 'count' && countParam
      ? Math.max(1, Number.parseInt(countParam, 10) || 0)
      : undefined;

  const order: FlashcardRunOrder | undefined =
    orderParam === 'due' || orderParam === 'mixed' || orderParam === 'new'
      ? orderParam
      : undefined;

  return {
    amount,
    count,
    type,
    order,
  };
};

export const hasSessionFlashcardRunSource = (url: URL) =>
  url.searchParams.get('source') === 'session';
